import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useKeapTagMapping } from '@/hooks/useKeapTagMapping';
import { settingsCache } from '@/utils/settingsCache';

interface UserProfile {
  is_admin: boolean;
  override_license_check: boolean;
}

export const KeapLicenseMappingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, session, profile } = useAuth();
  const { performTagMapping } = useKeapTagMapping();
  const [hasCheckedMapping, setHasCheckedMapping] = useState(false);

  useEffect(() => {
    const checkAndRunMapping = async () => {
      // Only run once per session and when user is logged in
      if (!user || !session || hasCheckedMapping) {
        return;
      }

      try {
        // Check if emergency failsafe is enabled using cache
        const isFailsafeEnabled = await settingsCache.getSetting(
          'license_mapping_failsafe_enabled',
          'false',
          5 * 60 * 1000 // 5 min TTL
        );

        if (isFailsafeEnabled === 'true') {
          // console.log('Emergency failsafe enabled - skipping Keap license mapping');
          setHasCheckedMapping(true);
          return;
        }

        // Check if Keap tag checking is enabled
        const isKeapTagCheckEnabled = await settingsCache.getSetting(
          'keap_tag_check_on_login',
          'false',
          5 * 60 * 1000 // 5 min TTL
        );

        if (isKeapTagCheckEnabled !== 'true') {
          // console.log('Keap tag check on login disabled - skipping Keap license mapping');
          setHasCheckedMapping(true);
          return;
        }

        // Use profile from AuthContext instead of querying DB
        const userProfile = profile;

        // Check conditions: user is not admin AND has not overridden license check
        if (
          userProfile &&
          !userProfile.is_admin &&
          !userProfile.override_license_check
        ) {
          console.log('Running Keap license mapping for user:', user.email);

          // Run mapping in background without blocking UI
          setTimeout(async () => {
            try {
              const result = await performTagMapping();
              // console.log('Keap license mapping completed:', result.success ? 'success' : 'failed');
            } catch (error) {
              // console.log('Keap license mapping error:', error);
            }
          }, 0);
        } else {
          // console.log('Skipping Keap license mapping - user is admin or has override enabled');
        }

        setHasCheckedMapping(true);
      } catch (error) {
        // console.error('Error in Keap license mapping check:', error);
        setHasCheckedMapping(true);
      }
    };

    checkAndRunMapping();
  }, [user, session, profile, performTagMapping, hasCheckedMapping]);

  // Reset check flag when user changes
  useEffect(() => {
    setHasCheckedMapping(false);
  }, [user?.id]);

  return <>{children}</>;
};
